import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import {
  fetchAnswerShareExportColumns,
  fetchAnswerSharePublic,
  unlockAnswerShare,
} from "../../api/answerShare";
import { ApiError } from "../../api/client";
import { Button } from "../../components/ui/button";
import { useCities } from "../../contexts/CitiesProvider";
import { usePastorDistricts } from "../../hooks/usePastorDistricts";
import { SubmissionsAnalytics } from "../../components/admin/SubmissionsAnalytics";
import { SubmissionsFieldsPicker } from "../../components/admin/SubmissionsFieldsPicker";
import { SubmissionsGroupBySelect } from "../../components/admin/SubmissionsGroupBySelect";
import { SubmissionsListToolbar } from "../../components/admin/SubmissionsListToolbar";
import { SubmissionsPrintControls } from "../../components/admin/SubmissionsPrintControls";
import { SubmissionsSortHeader } from "../../components/admin/SubmissionsSortHeader";
import {
  ANSWER_SHARE_SUBMISSIONS_PER_PAGE,
  useAnswerShareSubmissionsQuery,
} from "../../hooks/useAnswerShareSubmissionsQuery";
import { filterItemsByCreatedAt } from "../../lib/dates/dateRange";
import { formatAnswerTimestamp } from "../../lib/dates/formatAnswerDate";
import { buildShareSubmissionColumns } from "../../lib/forms/buildShareSubmissionColumns";
import { getSubmissionFieldValue } from "../../lib/forms/normalizeSubmission";
import {
  formatSubmissionGroupValue,
  formatSubmissionValue,
} from "../../lib/forms/formatSubmissionValue";
import {
  DEFAULT_SUBMISSIONS_SORT,
  SUBMISSIONS_DATE_COLUMN_KEY,
  SUBMISSIONS_ID_COLUMN_KEY,
  SUBMISSIONS_ROW_COLUMN_KEY,
  buildSubmissionsAnalytics,
  buildSubmissionsTableView,
  groupableSubmissionFields,
  submissionsRowNumbers,
  type SubmissionsSortState,
} from "../../lib/forms/submissionsTableView";
import {
  clearShareAccessSession,
  getShareAccessSession,
  saveShareAccessSession,
} from "../../lib/answerShare/session";
import type { AnswerSharePublicInfo } from "../../types/answerShare";
import type {
  FormSubmissionDto,
  SubmissionsExportColumns,
} from "../../types/submission";
import { FieldLabel, TextInput } from "../admin/_components/AdminShell";
import { FormsListPagination } from "../admin/forms-list/FormsListPagination";
import { AnswerShareExport } from "./AnswerShareExport";

const EMPTY_EXPORT_COLUMNS: SubmissionsExportColumns = { meta: [], fields: [] };

function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function submissionCreatedAt(raw: {
  createdAt?: string;
  created_at?: string;
}): string | undefined {
  return raw.createdAt ?? raw.created_at;
}

export default function AnswerSharePage() {
  const { cities } = useCities();
  const pastorDistricts = usePastorDistricts();
  const cityNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const city of cities) {
      map[String(city.id)] = city.name;
    }
    return map;
  }, [cities]);

  const { token: rawToken } = useParams<{ token: string }>();
  const token = rawToken?.trim() ?? "";

  const [publicInfo, setPublicInfo] = useState<AnswerSharePublicInfo | null>(
    null,
  );
  const [publicError, setPublicError] = useState<string | null>(null);
  const [publicLoading, setPublicLoading] = useState(true);

  const [accessToken, setAccessToken] = useState<string | null>(() =>
    token ? (getShareAccessSession(token)?.accessToken ?? null) : null,
  );
  const [password, setPassword] = useState("");
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(
    ANSWER_SHARE_SUBMISSIONS_PER_PAGE[0],
  );
  const [sort, setSort] = useState<SubmissionsSortState>(
    DEFAULT_SUBMISSIONS_SORT,
  );
  const [groupByFieldId, setGroupByFieldId] = useState<number | null>(null);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<Set<string>>(
    () => new Set([SUBMISSIONS_ID_COLUMN_KEY]),
  );
  const [shareTab, setShareTab] = useState<"list" | "analytics">("list");
  const [exportColumns, setExportColumns] =
    useState<SubmissionsExportColumns>(EMPTY_EXPORT_COLUMNS);
  const [exportColumnsLoading, setExportColumnsLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  const {
    allSubmissions,
    isLoading,
    error: submissionsError,
    accessExpired,
  } = useAnswerShareSubmissionsQuery({
    token,
    accessToken,
    page,
    perPage,
    enabled: Boolean(token && accessToken),
  });

  useEffect(() => {
    if (!token) {
      setPublicLoading(false);
      setPublicError("Ссылка недоступна");
      return;
    }

    let cancelled = false;
    setPublicLoading(true);
    setPublicError(null);

    void fetchAnswerSharePublic(token)
      .then((info) => {
        if (!cancelled) setPublicInfo(info);
      })
      .catch((err) => {
        if (!cancelled) {
          setPublicError(
            err instanceof ApiError
              ? err.message || "Ссылка недоступна"
              : "Ссылка недоступна",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPublicLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (publicInfo?.formName) {
      document.title = `Ответы · ${publicInfo.formName}`;
    }
  }, [publicInfo?.formName]);

  useEffect(() => {
    if (!accessExpired || !token) return;
    clearShareAccessSession(token);
    setAccessToken(null);
  }, [accessExpired, token]);

  useEffect(() => {
    if (!token || !accessToken) {
      setExportColumns(EMPTY_EXPORT_COLUMNS);
      setExportColumnsLoading(false);
      return;
    }

    let cancelled = false;
    setExportColumnsLoading(true);

    void fetchAnswerShareExportColumns(token, accessToken)
      .then((columns) => {
        if (!cancelled) setExportColumns(columns);
      })
      .catch((err) => {
        if (cancelled) return;
        setExportColumns(EMPTY_EXPORT_COLUMNS);
        if (err instanceof ApiError && err.status === 401) {
          clearShareAccessSession(token);
          setAccessToken(null);
        }
      })
      .finally(() => {
        if (!cancelled) setExportColumnsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, accessToken]);

  const handleUnlock = async (event: FormEvent) => {
    event.preventDefault();
    if (!token || !password.trim()) return;

    setUnlocking(true);
    setUnlockError(null);

    try {
      const response = await unlockAnswerShare(token, password.trim());
      saveShareAccessSession(token, {
        accessToken: response.accessToken,
        expiresAt: response.expiresAt,
      });
      setAccessToken(response.accessToken);
      setPassword("");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUnlockError("Неверный пароль");
      } else if (err instanceof ApiError && err.status === 422) {
        setUnlockError(err.message || "Не удалось открыть доступ");
      } else if (err instanceof ApiError && err.status === 404) {
        setUnlockError("Ссылка недоступна");
      } else {
        setUnlockError("Не удалось открыть доступ");
      }
    } finally {
      setUnlocking(false);
    }
  };

  const scopedSubmissions = useMemo(
    () =>
      filterItemsByCreatedAt(
        allSubmissions,
        rangeFrom,
        rangeTo,
        submissionCreatedAt,
      ),
    [allSubmissions, rangeFrom, rangeTo],
  );
  const fieldColumns = useMemo(
    () => buildShareSubmissionColumns(exportColumns.fields, allSubmissions),
    [exportColumns.fields, allSubmissions],
  );
  const builderFields = useMemo(
    () => fieldColumns.map((column) => column.field),
    [fieldColumns],
  );
  const groupableFields = useMemo(
    () => groupableSubmissionFields(builderFields),
    [builderFields],
  );
  const showRowColumn = !hiddenColumnKeys.has(SUBMISSIONS_ROW_COLUMN_KEY);
  const showIdColumn = !hiddenColumnKeys.has(SUBMISSIONS_ID_COLUMN_KEY);
  const showDateColumn = !hiddenColumnKeys.has(SUBMISSIONS_DATE_COLUMN_KEY);
  const visibleFieldColumns = useMemo(
    () =>
      fieldColumns.filter((field) => !hiddenColumnKeys.has(String(field.id))),
    [fieldColumns, hiddenColumnKeys],
  );
  const fieldOptions = useMemo(
    () => [
      { id: SUBMISSIONS_ROW_COLUMN_KEY, label: "Номер ответа" },
      { id: SUBMISSIONS_ID_COLUMN_KEY, label: "ID ответа" },
      { id: SUBMISSIONS_DATE_COLUMN_KEY, label: "Дата ответа" },
      ...fieldColumns.map((field) => ({
        id: String(field.id),
        label: field.label,
      })),
    ],
    [fieldColumns],
  );
  const getFieldValue = useMemo(
    () => (submission: FormSubmissionDto, fieldId: number) => {
      const column = fieldColumns.find((item) => item.id === fieldId);
      if (!column) return "—";
      return formatSubmissionValue(
        column.field,
        getSubmissionFieldValue(submission.data, column.field, builderFields),
        {
          cityNames,
          ages: submission.ages,
          asOf: submission.created_at ?? submission.createdAt,
        },
      );
    },
    [builderFields, cityNames, fieldColumns],
  );
  const getGroupValue = useMemo(
    () => (submission: FormSubmissionDto, fieldId: number) => {
      const column = fieldColumns.find((item) => item.id === fieldId);
      if (!column) return "—";
      return formatSubmissionGroupValue(
        column.field,
        getSubmissionFieldValue(submission.data, column.field, builderFields),
        {
          cityNames,
          ages: submission.ages,
          asOf: submission.created_at ?? submission.createdAt,
        },
      );
    },
    [builderFields, cityNames, fieldColumns],
  );
  const view = useMemo(
    () =>
      buildSubmissionsTableView(scopedSubmissions, {
        sort,
        groupByFieldId,
        page,
        perPage,
        getFieldValue: getGroupValue,
      }),
    [getGroupValue, groupByFieldId, page, perPage, scopedSubmissions, sort],
  );
  const meta = view.meta;
  const tableColSpan = Math.max(
    1,
    (showRowColumn ? 1 : 0) +
      (showIdColumn ? 1 : 0) +
      (showDateColumn ? 1 : 0) +
      visibleFieldColumns.length,
  );
  const rowNumberById = useMemo(
    () => submissionsRowNumbers(view.pageItems, meta.current_page, perPage),
    [meta.current_page, perPage, view.pageItems],
  );
  const analytics = useMemo(
    () =>
      buildSubmissionsAnalytics(
        scopedSubmissions,
        builderFields,
        getGroupValue,
      ),
    [builderFields, getGroupValue, scopedSubmissions],
  );

  useEffect(() => {
    if (page > meta.last_page) setPage(meta.last_page);
  }, [meta.last_page, page]);

  useEffect(() => {
    if (
      groupByFieldId != null &&
      !groupableFields.some((field) => field.id === groupByFieldId)
    ) {
      setGroupByFieldId(null);
    }
  }, [groupByFieldId, groupableFields]);

  const handleSortChange = (next: SubmissionsSortState) => {
    setSort(next);
    setPage(1);
  };

  const handleGroupByChange = (fieldId: number | null) => {
    setGroupByFieldId(fieldId);
    setPage(1);
  };

  const handleRangeChange = (range: { from: string; to: string }) => {
    setRangeFrom(range.from);
    setRangeTo(range.to);
    setPage(1);
  };

  const handleAccessExpired = () => {
    if (!token) return;
    clearShareAccessSession(token);
    setAccessToken(null);
  };

  if (!token) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <p className="font-onest text-sm text-slate-600">Ссылка недоступна</p>
      </div>
    );
  }

  if (publicLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <p className="font-onest text-sm text-slate-500">Загрузка…</p>
      </div>
    );
  }

  if (publicError) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="font-unbounded text-xl font-bold text-ink">
          Ссылка недоступна
        </h1>
        <p className="mt-2 font-onest text-sm text-slate-600">{publicError}</p>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="mx-auto flex min-h-[70vh] w-full max-w-md flex-col justify-center px-4 py-12">
        <div className="mb-6 text-center">
          <p className="font-unbounded text-xl font-extrabold text-ink">
            {publicInfo?.formName ?? "Ответы формы"}
          </p>
          {publicInfo?.expiresAt ? (
            <p className="mt-2 font-onest text-sm text-slate-500">
              Доступ до {formatDateTime(publicInfo.expiresAt)}
            </p>
          ) : null}
        </div>

        <form
          onSubmit={(event) => void handleUnlock(event)}
          className="rounded-2xl border border-slate-200/80 bg-white/90 p-6 shadow-sm"
        >
          <p className="mb-4 font-onest text-sm text-slate-600">
            Введите пароль, который вам передали вместе со ссылкой.
          </p>

          {unlockError ? (
            <div
              className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2"
              role="alert"
            >
              <p className="font-onest text-sm text-red-700">{unlockError}</p>
            </div>
          ) : null}

          <FieldLabel htmlFor="share-password">Пароль</FieldLabel>
          <TextInput
            id="share-password"
            type="password"
            value={password}
            onChange={setPassword}
            autoFocus
          />

          <Button
            type="submit"
            className="mt-5 w-full"
            disabled={unlocking || !password.trim()}
          >
            {unlocking ? "Проверка…" : "Открыть ответы"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
      <header className="mb-6">
        <h1 className="font-unbounded text-2xl font-extrabold text-ink">
          {publicInfo?.formName ?? "Ответы формы"}
        </h1>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className="font-onest text-sm text-slate-700">
            Всего ответов:{" "}
            <span className="font-bold tabular-nums text-ink">
              {meta.total}
            </span>
          </p>
          <div
            role="tablist"
            aria-label="Разделы ответов"
            className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50/80 p-1"
          >
            {(
              [
                { id: "list", label: "Список ответов" },
                { id: "analytics", label: "Аналитика" },
              ] as const
            ).map((tab) => {
              const selected = shareTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setShareTab(tab.id)}
                  className={[
                    "rounded-md px-3 py-1.5 font-onest text-sm font-medium transition-colors",
                    selected
                      ? "bg-white text-ink shadow-sm"
                      : "text-slate-600 hover:text-slate-900",
                  ].join(" ")}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <section className="overflow-visible rounded-xl border border-slate-200/80 bg-white shadow-card-lg">
        {submissionsError || shareError ? (
          <div
            className="mx-4 mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3"
            role="alert"
          >
            <p className="font-onest text-sm text-red-700">
              {submissionsError || shareError}
            </p>
          </div>
        ) : null}

        {shareTab === "analytics" ? (
          isLoading && allSubmissions.length === 0 ? (
            <div className="animate-pulse space-y-3 p-4">
              <div className="h-8 rounded-lg bg-slate-100" />
              <div className="h-24 rounded-lg bg-slate-100" />
            </div>
          ) : allSubmissions.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="font-onest text-sm text-slate-600">
                Пока нет ответов
              </p>
            </div>
          ) : scopedSubmissions.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="font-onest text-sm text-slate-600">
                Нет ответов за выбранный период
              </p>
            </div>
          ) : (
            <SubmissionsAnalytics
              fields={analytics}
              groupableFields={groupableFields}
              submissions={scopedSubmissions}
              getFieldValue={getGroupValue}
              cities={cities}
              pastorDistricts={pastorDistricts}
              onSelectField={(fieldId) => {
                setGroupByFieldId(fieldId);
                setShareTab("list");
                setPage(1);
              }}
            />
          )
        ) : (
          <>
            <SubmissionsListToolbar>
              <AnswerShareExport
                token={token}
                accessToken={accessToken}
                columns={exportColumns}
                columnsLoading={exportColumnsLoading}
                from={rangeFrom}
                to={rangeTo}
                onRangeChange={handleRangeChange}
                onAccessExpired={handleAccessExpired}
                onError={setShareError}
                extras={
                  <SubmissionsFieldsPicker
                    fields={fieldOptions}
                    hiddenIds={hiddenColumnKeys}
                    onChange={setHiddenColumnKeys}
                  />
                }
              />
              <SubmissionsGroupBySelect
                fields={groupableFields}
                value={groupByFieldId}
                onChange={handleGroupByChange}
              />
              <SubmissionsPrintControls
                formName={publicInfo?.formName ?? "Ответы формы"}
                submissions={view.sorted}
                fields={builderFields}
                visibleFields={visibleFieldColumns.map(
                  (column) => column.field,
                )}
                cityNames={cityNames}
                groupByFieldId={groupByFieldId}
                getGroupValue={getGroupValue}
                showRow={showRowColumn}
                showId={showIdColumn}
                showDate={showDateColumn}
              />
            </SubmissionsListToolbar>

            {isLoading && allSubmissions.length === 0 ? (
              <div className="animate-pulse space-y-3 p-4">
                <div className="h-8 rounded-lg bg-slate-100" />
                <div className="h-24 rounded-lg bg-slate-100" />
              </div>
            ) : allSubmissions.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <p className="font-onest text-sm text-slate-600">
                  Пока нет ответов
                </p>
              </div>
            ) : scopedSubmissions.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <p className="font-onest text-sm text-slate-600">
                  Нет ответов за выбранный период
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/70">
                      {showRowColumn ? (
                        <th className="w-12 px-3 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          №
                        </th>
                      ) : null}
                      {showIdColumn ? (
                        <SubmissionsSortHeader
                          label="ID ответа"
                          sortKey={{ kind: "id" }}
                          sort={sort}
                          onSortChange={handleSortChange}
                        />
                      ) : null}
                      {showDateColumn ? (
                        <SubmissionsSortHeader
                          label="Дата ответа"
                          sortKey={{ kind: "createdAt" }}
                          sort={sort}
                          onSortChange={handleSortChange}
                        />
                      ) : null}
                      {visibleFieldColumns.map((field) => (
                        <SubmissionsSortHeader
                          key={field.id}
                          label={field.label}
                          sortKey={{ kind: "field", fieldId: field.id }}
                          sort={sort}
                          onSortChange={handleSortChange}
                        />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {view.pageRows.map((row) => {
                      if (row.type === "group") {
                        return (
                          <tr
                            key={`group-${row.key}`}
                            className="bg-slate-50/90"
                          >
                            <td
                              colSpan={tableColSpan}
                              className="px-3 py-2 font-onest text-xs font-semibold text-slate-700"
                            >
                              {row.label}
                              <span className="ml-2 font-normal text-slate-500">
                                {row.count}
                              </span>
                            </td>
                          </tr>
                        );
                      }

                      const submission = row.submission;
                      return (
                        <tr
                          key={submission.id}
                          className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70"
                        >
                          {showRowColumn ? (
                            <td className="whitespace-nowrap px-3 py-2 font-onest text-xs font-medium text-slate-700">
                              {rowNumberById.get(submission.id)}
                            </td>
                          ) : null}
                          {showIdColumn ? (
                            <td className="whitespace-nowrap px-3 py-2 font-onest text-xs text-slate-700">
                              {submission.id}
                            </td>
                          ) : null}
                          {showDateColumn ? (
                            <td className="whitespace-nowrap px-3 py-2 font-onest text-xs text-slate-600">
                              {formatAnswerTimestamp(
                                submissionCreatedAt(submission),
                              )}
                            </td>
                          ) : null}
                          {visibleFieldColumns.map((field) => (
                            <td
                              key={field.id}
                              className="max-w-[16rem] px-3 py-2 font-onest text-xs text-slate-700"
                            >
                              <span className="line-clamp-3 break-words">
                                {getFieldValue(submission, field.id)}
                              </span>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <FormsListPagination
              meta={meta}
              page={page}
              perPage={perPage}
              perPageOptions={ANSWER_SHARE_SUBMISSIONS_PER_PAGE}
              isLoading={isLoading}
              onPageChange={setPage}
              onPerPageChange={(next) => {
                setPerPage(next);
                setPage(1);
              }}
            />
          </>
        )}
      </section>
    </div>
  );
}
